import { Component } from "react";
import { observer } from "mobx-react";

import type { Message } from "../models.ts";
import { ThinkingIndicator } from "./ThinkingIndicator.tsx";
import { ImgCancel } from "./Images/ImgCancel.tsx";
import { ChatMessageSearchProgress } from "./ChatMessageSearchProgress.tsx";
import { CollapsibleThink } from "./CollapsibleThink.tsx";

type ChatMessageProgressProps = {
  message: Message;
  executingNode: string | null;
  previewBlobUrl: string | null;
  progress: { current: number; max: number } | null;
  promptId: string | null;
  onCancel: () => void;
};

@observer
export class ChatMessageProgress extends Component<ChatMessageProgressProps> {
  render() {
    const { message, executingNode, previewBlobUrl, progress, promptId, onCancel } = this.props;

    let showThinking = true;
    if (message.collapsedMessages) {
      const hasToolMessage = message.collapsedMessages.some(m => m.role === 'tool' && !m.content);
      showThinking = !hasToolMessage;
    }

    const showExecutingNode = !!executingNode;
    const hasToolProgress = message.toolProgress && (!!message.toolProgress.searchResults?.length || !!(message.toolProgress.sitesProcessing?.length));
    const operationId = message.toolProgress?.operationId || promptId;
    const statusText = message.toolProgress?.status;

    const isWaitingForServerUpdates = !previewBlobUrl && !executingNode && !progress && !hasToolProgress && !statusText;
    const streamingText = message.toolProgress?.streamingText;
    const thinkingText = message.toolProgress?.thinkingText;

    return (<>
      {isWaitingForServerUpdates && !streamingText && <ThinkingIndicator text="Waiting for server updates..." />}
      {statusText && <ThinkingIndicator text={statusText} />}
      {!isWaitingForServerUpdates && !statusText && showThinking && !showExecutingNode && !hasToolProgress && !streamingText && <ThinkingIndicator text="Executing tool" />}
      {showExecutingNode && <div className="executing-node-info">
        <ThinkingIndicator text={`Executing: ${executingNode}`} />
      </div>}

      {/* Sub-agent thinking block (per-iteration, live) */}
      {thinkingText && (
        <CollapsibleThink text="Sub-agent thinking" isAnimating className="sub-agent-think">
          <pre className="sub-agent-think-content">{thinkingText}</pre>
        </CollapsibleThink>
      )}

      {/* Streaming transcription / sub-agent content text */}
      {streamingText && <div className="tool-streaming-text">{streamingText}</div>}

      {/* Web search progress display */}
      {hasToolProgress && <ChatMessageSearchProgress message={message} />}

      {previewBlobUrl && <div className="tool-progress">
        <img className="tool-progress-preview" src={previewBlobUrl} alt="Tool progress" />
        {progress && <div className="tool-progress-bar">
          <div className="tool-progress-bar-inner" style={{ width: `${progress.current / progress.max * 100}%` }}></div>
        </div>}
        {promptId && <div className="tool-progress-cancel">
          <button onClick={onCancel}>
            <ImgCancel size={20} />
          </button>
        </div>}
      </div>}

      {/* Cancel button for web search */}
      {hasToolProgress && operationId && <div className="tool-progress-cancel search-cancel">
        <button onClick={onCancel} title="Cancel search">
          <ImgCancel size={20} />
          <span>Cancel</span>
        </button>
      </div>}
    </>);
  }
}
